import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Card, Spinner, EmptyState } from "@/components/bits";
import StatusBadge from "@/components/StatusBadge";
import PartPhotos from "@/components/PartPhotos";
import { Input } from "@/components/ui/input";
import { Search, Car, ChevronLeft, Clock } from "lucide-react";

const STAGES = ["SPK FIX", "PART REQUIRED", "ORDERED", "RECEIVED", "WAREHOUSE", "ISSUED", "INSTALLED", "SALVAGE", "CLOSED"];

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).replace(/\./g, ":"); } catch { return ts; }
}

export default function History() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [openPart, setOpenPart] = useState(null);

  const search = useCallback(() => {
    setLoading(true);
    api.get(`/history/search?q=${encodeURIComponent(q)}`).then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [q]);
  useEffect(() => { const t = setTimeout(search, 300); return () => clearTimeout(t); }, [search]);

  const open = useCallback((jobId) => { setDetail("loading"); setOpenPart(null); api.get(`/history/claim/${jobId}`).then((r) => setDetail(r.data)); }, []);

  // Auto-open claim detail when navigated with ?job=<id> (from Dashboard / Vehicle List)
  useEffect(() => {
    const jobId = searchParams.get("job");
    if (jobId) open(jobId);
  }, [searchParams, open]);

  const closeDetail = () => { setDetail(null); if (searchParams.get("job")) setSearchParams({}, { replace: true }); };

  if (detail) {
    if (detail === "loading") return <div><PageHeader title="History" /><Spinner /></div>;
    const { header, parts, events } = detail;
    return (
      <div>
        <button onClick={closeDetail} className="flex items-center text-sm text-gray-500 mb-2" data-testid="history-back"><ChevronLeft size={16} />Kembali</button>
        <Card className="p-4 mb-3" data-testid="history-header">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span className="font-mono-data font-bold text-lg text-gray-900">{header.nopol}</span>
            <span className="text-gray-300">|</span><span className="font-semibold">{header.vehicle || "—"}</span>
            <span className="text-gray-300">|</span><span>{header.customer || "—"}</span>
            <span className="text-gray-300">|</span><span>{header.insurance || "—"}</span>
            <span className="text-gray-300">|</span><span className="font-mono-data">{header.spk_number || header.claim_number || "—"}</span>
            <StatusBadge status={header.status} />
          </div>
        </Card>

        <Card className="overflow-hidden mb-3">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-500 uppercase"><tr>
                <th className="text-left px-2 py-2">Part No</th><th className="text-left px-2 py-2">Nama</th>
                <th className="text-center px-2 py-2">Req</th><th className="text-center px-2 py-2">Ord</th>
                <th className="text-center px-2 py-2">Recv</th><th className="text-center px-2 py-2">WH</th>
                <th className="text-center px-2 py-2">Iss</th><th className="text-center px-2 py-2">Inst</th>
                <th className="text-center px-2 py-2">Salv</th><th className="text-left px-2 py-2">Status</th>
              </tr></thead>
              <tbody>
                {parts.map((p) => (
                  <tr key={p.id} className="border-t border-gray-100 cursor-pointer hover:bg-gray-50" onClick={() => setOpenPart(openPart === p.id ? null : p.id)} data-testid={`history-part-${p.id}`}>
                    <td className="px-2 py-1.5 font-mono-data">{p.part_number || "—"}</td>
                    <td className="px-2 py-1.5">{p.part_name}</td>
                    <td className="text-center px-2 py-1.5">{p.required_qty}</td><td className="text-center px-2 py-1.5">{p.ordered_qty}</td>
                    <td className="text-center px-2 py-1.5">{p.received_qty}</td><td className="text-center px-2 py-1.5">{p.warehouse_qty}</td>
                    <td className="text-center px-2 py-1.5">{p.issued_qty}</td><td className="text-center px-2 py-1.5">{p.installed_qty}</td>
                    <td className="text-center px-2 py-1.5">{p.salvage_qty}</td>
                    <td className="px-2 py-1.5"><StatusBadge status={p.over_received ? "OVER" : p.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {openPart && (() => { const p = parts.find((x) => x.id === openPart); return (
            <div className="p-3 bg-gray-50 border-t" data-testid="history-part-detail">
              <div className="text-xs font-semibold mb-2">{p.part_number || "—"} — {p.part_name} • Supplier: {p.supplier || "—"} • ETD: {(p.etd || "").slice(0, 10) || "—"}</div>
              <div className="flex flex-wrap gap-4">
                <div><div className="text-[11px] text-gray-500 mb-1">Foto Awal</div><PartPhotos partLineId={p.id} phase="INITIAL" canUpload={false} compact /></div>
                <div><div className="text-[11px] text-gray-500 mb-1">Foto Terpasang</div><PartPhotos partLineId={p.id} phase="INSTALLED" canUpload={false} compact /></div>
                <div><div className="text-[11px] text-gray-500 mb-1">Foto Akhir</div><PartPhotos partLineId={p.id} phase="FINAL" canUpload={false} compact /></div>
              </div>
            </div>
          ); })()}
        </Card>

        <Card className="p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center gap-1"><Clock size={13} /> Timeline Perjalanan Part</div>
          <ol className="relative border-l-2 border-gray-200 ml-2" data-testid="history-timeline">
            {events.length === 0 ? <li className="ml-4 text-sm text-gray-400">Belum ada event transaksi</li> :
              events.map((e, i) => (
                <li key={i} className="ml-4 mb-3">
                  <div className="absolute -left-[7px] h-3 w-3 rounded-full bg-[var(--gold)]" />
                  <div className="text-sm font-semibold text-gray-900">{e.stage} {e.qty ? <span className="text-gray-500 font-normal">×{e.qty}</span> : ""}</div>
                  <div className="text-xs text-gray-500">{fmt(e.date)} • {e.user || "—"} {e.detail ? `• ${e.detail}` : ""} {e.status ? `• ${e.status}` : ""}</div>
                </li>
              ))}
          </ol>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="History" subtitle="Lacak perjalanan sparepart per kendaraan & claim/SPK. Read-only audit trail." />
      <div className="relative mb-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari Nopol / No SPK / Customer / Asuransi…" className="pl-9" data-testid="history-search-input" />
      </div>
      {loading ? <Spinner /> : rows.length === 0 ? <EmptyState title="Tidak ada data" icon={Car} /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>
                <th className="text-left px-3 py-2">Nopol</th><th className="text-left px-3 py-2">Mobil</th>
                <th className="text-left px-3 py-2">Customer</th><th className="text-left px-3 py-2">Asuransi</th>
                <th className="text-left px-3 py-2">SPK/Claim</th><th className="text-center px-2 py-2">Part</th>
                <th className="text-center px-2 py-2">Ord</th><th className="text-center px-2 py-2">Recv</th>
                <th className="text-center px-2 py-2">Inst</th><th className="text-left px-3 py-2">Status</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.job_id} className="border-t border-gray-100 cursor-pointer hover:bg-gray-50" onClick={() => open(r.job_id)} data-testid={`history-row-${r.job_id}`}>
                    <td className="px-3 py-2 font-mono-data font-semibold">{r.nopol}</td>
                    <td className="px-3 py-2">{r.vehicle || "—"}</td>
                    <td className="px-3 py-2">{r.customer || "—"}</td>
                    <td className="px-3 py-2">{r.insurance || "—"}</td>
                    <td className="px-3 py-2 font-mono-data">{r.spk_number || r.claim_number || "—"}</td>
                    <td className="text-center px-2 py-2">{r.summary.part}</td>
                    <td className="text-center px-2 py-2">{r.summary.ordered}</td>
                    <td className="text-center px-2 py-2">{r.summary.received}</td>
                    <td className="text-center px-2 py-2">{r.summary.installed}</td>
                    <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
